/**
 * NotificationsWidget — the unread pile, on the page you land on.
 *
 * It costs NOTHING to render: the notification centre is already synced into
 * redux app-wide (`useNotificationSync` + the PubSub `notifications` hub), so
 * this card is a pure read of state that exists whether or not the widget is
 * on the dashboard. That is the whole reason it can be a widget at all — a
 * card that had to fetch its own list would be a second request for something
 * the bell icon already has.
 *
 * Unread first, then the most recent read ones as context, because a
 * notifications card that empties itself the moment you catch up reads as
 * broken rather than as calm.
 */

import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiBellLight,
  PiBellSimpleLight,
  PiCheckLight,
} from 'react-icons/pi';
import { useAppDispatch, useAppSelector, RootState } from '../../../store';
import GlassCard from '../../shared/brand/GlassCard';
import { Well } from '../../shared/brand/Field';
import Button from '../../shared/TailwindComponents/Button';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import { formatRelativeTime, formatAbsoluteTime } from '../../shared/feed/feedTime';
import { AppNotification, toggleSidepanel } from '../../Notifications/notificationSlice';

/** How many rows the card shows before it defers to the centre. */
const MAX_ROWS = 6;

/**
 * Unread first, newest first within each group.
 *
 * Sorting a copy: the slice's array is redux state, and `sort` is in place.
 */
const rank = (notifications: AppNotification[]): AppNotification[] =>
  [...notifications].sort((a, b) => {
    if (a.is_read !== b.is_read) return a.is_read ? 1 : -1;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });

const NotificationsWidget: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const notifications = useAppSelector(
    (state: RootState) => state.notifications.notifications
  );
  const unreadCount = useAppSelector(
    (state: RootState) => state.notifications.unreadCount
  );

  const rows = useMemo(() => rank(notifications || []).slice(0, MAX_ROWS), [notifications]);

  return (
    <GlassCard
      icon={<PiBellLight />}
      title={
        <>
          {t('Notifications')}
          {unreadCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-champagne/20 px-1.5 text-xs text-fg-strong">
              {unreadCount}
            </span>
          )}
        </>
      }
      actions={
        <Button variant="outline" size="small" onClick={() => dispatch(toggleSidepanel())}>
          {t('Open')}
        </Button>
      }
      padding={rows.length === 0 ? 'none' : 'sm'}
      className="overflow-hidden"
    >
      {rows.length === 0 ? (
        <EmptyState
          density="compact"
          icon={<PiBellSimpleLight />}
          headline={t('Nothing needs you right now')}
          subtext={t('New activity on your work will show up here.')}
        />
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((notification) => (
            <Well
              key={notification.notification_id}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (notification.link) history.push(notification.link);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && notification.link) history.push(notification.link);
              }}
              className={clsx(
                'flex cursor-pointer items-start gap-3 transition-colors duration-200 ease-slide',
                'hover:border-champagne/50 focus:outline-none focus:border-champagne/60'
              )}
            >
              {/* The champagne dot is the ONLY accent on the row — an unread
                  pile drawn in full colour is a wall, not a signal. */}
              <span
                aria-hidden
                className={clsx(
                  'mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full',
                  notification.is_read ? 'bg-hairline' : 'bg-champagne'
                )}
              />
              <span className="min-w-0 flex-1">
                {notification.title && (
                  <span className="block truncate text-sm font-medium text-gray-700 dark:text-gray-200">
                    {notification.title}
                  </span>
                )}
                <span className="block text-sm text-gray-500 line-clamp-2 dark:text-gray-400">
                  {notification.content}
                </span>
                <span
                  className="mt-0.5 block text-[11px] text-gray-400 dark:text-gray-500"
                  title={formatAbsoluteTime(notification.created_at || undefined)}
                >
                  {formatRelativeTime(notification.created_at || undefined, t)}
                </span>
              </span>
              {notification.is_read && (
                <PiCheckLight
                  aria-hidden
                  className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600"
                />
              )}
            </Well>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

export default NotificationsWidget;
